import { EXTERNAL_ROUTES, ROUTES } from '@ab-core/functions';
import type { MappedProduct } from '@ab-core/graphql/dist';
import useText from '@ab-core/localization/src/useText';
import type { TagProps } from '@ab-core/tags';
import { Tag, TAG_TYPE } from '@ab-core/tags';
import type { WithTestId } from '@ab-core/testing';
import { getTestIdProp } from '@ab-core/testing';
import { withPrefix } from 'gatsby';
import type { FC } from 'react';
import React from 'react';
import type { GetProductTagsPropsProps } from './getProductTagsProps';
import { getFallbackState, getProductTagsProps } from './getProductTagsProps';
import { Image, ImageContainer, ProductTagsContainer } from './styled';
import { getStandardDynamicTagText } from './utils';

export type ProductTagsProps = {
    isAktion: boolean;
    isPremium: boolean;
    isTecselect: boolean;
    isAbakus: boolean;
    isAbakusPlus: boolean;
    isFiveYearGuarantee: boolean;
    is125Vip: boolean;
    is125Premium: boolean;
    isAdditionalSalesPromotion: boolean;
    scoreCardIndex?: number;
    purchase?: boolean;
    isItShop?: boolean;
    successorProduct?: MappedProduct['successorProduct'];
    ean?: MappedProduct['ean'];
};

export type DynamicTextTagProps = Omit<TagProps, 'text'> &
    WithTestId & {
        shortText: TagProps['text'];
        longText: TagProps['text'];
    };

type ImageTagProps = React.ComponentPropsWithoutRef<'img'> & {
    href: string;
};

const LONG_TEXT_THRESHOLD = 4;
const MIN_TAGS_WITH_LONG_TEXT = 1;

const selectAndGetUserTag = (
    props: Pick<ProductTagsProps, 'isAbakus' | 'isAbakusPlus'>
): DynamicTextTagProps | null => {
    const { isAbakus, isAbakusPlus } = props;

    if (isAbakusPlus) {
        return {
            ...getStandardDynamicTagText({ iconName: 'AbakusPlus', text: 'Abakus+' }),
            type: TAG_TYPE.TERTIARY
        };
    }

    if (isAbakus) {
        return {
            ...getStandardDynamicTagText({ iconName: 'Abakus', text: 'Abakus' }),
            type: TAG_TYPE.TERTIARY
        };
    }

    return null;
};

export const ProductTags: FC<ProductTagsProps> = (props) => {
    const {
        isAktion,
        isPremium,
        isTecselect,
        isAbakus,
        isAbakusPlus,
        scoreCardIndex,
        is125Vip,
        is125Premium,
        isAdditionalSalesPromotion,
        isFiveYearGuarantee,
        purchase,
        isItShop,
        successorProduct,
        ean
    } = props;
    const link1 = useText('itshop.link1');

    const dynamicTags: Array<DynamicTextTagProps> = [];
    const imageTags: Array<ImageTagProps> = [];

    const getItShopLink = (productEan?: string) => {
        if (!productEan) {
            return EXTERNAL_ROUTES.ITSHOP_LOGIN;
        }

        return `${link1}${productEan}`;
    };

    if (isAktion) {
        dynamicTags.push(getStandardDynamicTagText({ iconName: 'Aktion', text: 'Aktion' }));
    }

    if (isPremium) {
        dynamicTags.push({
            ...getStandardDynamicTagText({ iconName: 'Diamond', text: 'Premium' }),
            type: TAG_TYPE.SECONDARY
        });
    }

    if (isFiveYearGuarantee) {
        dynamicTags.push({
            ...getStandardDynamicTagText({
                iconName: 'Five',
                text: '5-jahres Garantie',
                href: withPrefix(`${ROUTES.FIVE_YEAR_GUARANTEE}`)
            }),
            type: TAG_TYPE.TERTIARY
        });
    }

    if (scoreCardIndex && scoreCardIndex > 0) {
        dynamicTags.push({
            ...getStandardDynamicTagText({
                iconName: 'Leaf',
                text: 'alexgreen',
                href: EXTERNAL_ROUTES.ALEXGREEN
            }),
            type: TAG_TYPE.SUCCESS
        });
    }

    const userTag = selectAndGetUserTag({ isAbakus, isAbakusPlus });

    if (userTag) {
        dynamicTags.push(userTag);
    }

    if (isTecselect) {
        dynamicTags.push({
            ...getStandardDynamicTagText({
                iconName: 'Tecselect',
                text: 'Tecselect',
                href: withPrefix(`${ROUTES.TECSELECT}`)
            }),
            type: TAG_TYPE.TERTIARY
        });
    }

    if (purchase) {
        dynamicTags.push({
            ...getStandardDynamicTagText({
                text: 'Bereits gekauft',
                iconName: 'Cart'
            }),
            type: TAG_TYPE.INFO
        });
    }

    if (isItShop) {
        dynamicTags.push({
            ...getStandardDynamicTagText({
                text: 'IT-Shop',
                iconName: 'ExternalLink',
                href: getItShopLink(ean)
            }),
            type: TAG_TYPE.INFO
        });
    }

    if (successorProduct) {
        dynamicTags.push({
            ...getStandardDynamicTagText({
                text: 'Nachfolger vorhanden',
                iconName: 'ArrowRight',
                href: `${withPrefix(`${ROUTES.PRODUCTPAGE}/${successorProduct}`)}`
            }),
            type: TAG_TYPE.WARNING
        });
    }

    if (is125Vip) {
        imageTags.push({
            src: `${withPrefix('/images/125_years/vip.png')}`,
            alt: '125 VIP',
            href: withPrefix(`${ROUTES.JUBILEE_CAMPAIGN}`)
        });
    }

    if (is125Premium) {
        imageTags.push({
            src: `${withPrefix('/images/125_years/premium.png')}`,
            alt: '125 Premium',
            href: withPrefix(`${ROUTES.JUBILEE_CAMPAIGN}`)
        });
    }

    if (isAdditionalSalesPromotion) {
        imageTags.push({
            src: `${withPrefix('/images/additionalSalesPromotion/AB_Badge_Mehrumsatz26.svg')}`,
            alt: 'Mehrumsatz Aktion 2026',
            href: EXTERNAL_ROUTES.MEHRUMSATZ_AKTION_2026
        });
    }

    const isOverThreshold = dynamicTags.length + imageTags.length > LONG_TEXT_THRESHOLD;

    const tags: Array<TagProps> = dynamicTags.map((dynamicTag, key) => {
        let useShortText = isOverThreshold;

        if (key < MIN_TAGS_WITH_LONG_TEXT - imageTags.length) {
            useShortText = false;
        }

        return {
            ...dynamicTag,
            ...getTestIdProp(dynamicTag.testId),
            text: useShortText ? dynamicTag.shortText : dynamicTag.longText
        };
    });

    return (
        <ProductTagsContainer>
            {imageTags.map((imageTag, key) => {
                return (
                    <ImageContainer key={key}>
                        <a href={imageTag.href} target="_blank" rel="noreferrer">
                            <Image {...imageTag} />
                        </a>
                    </ImageContainer>
                );
            })}

            {tags.map((tag, key) => (
                <Tag key={key} {...tag} />
            ))}
        </ProductTagsContainer>
    );
};

export { getFallbackState, getProductTagsProps };

export type { GetProductTagsPropsProps };
